import AnnouncementBar from '../home/components/AnnouncementBar';
import Navbar from '../home/components/Navbar';
import FooterSection from '../home/components/FooterSection';
import SEOHead from '../../components/feature/SEOHead';
import { SITE_URL } from '../../utils/seo';

const challenges = [
  { icon: 'ri-money-dollar-circle-line', title: 'Artan Maliyetler', stat: '%46', statLabel: 'lisanslama maliyet artışı öngörüyor', desc: 'Hiper yönetici lisanslama maliyetleri artık öngörülüyor. %65\'i genel bulut maliyetlerini kontrol altına almayı kritik sorun olarak görüyor.' },
  { icon: 'ri-question-line', title: 'Belirsiz Gelecek', stat: '%54', statLabel: 'alternatifi değerlendiriyor', desc: '%54\'ü endişeler nedeniyle aktif olarak alternatif değerlendiriyor.' },
  { icon: 'ri-cloud-line', title: 'Bulut Karmaşası', stat: '%76', statLabel: 'bulut stratejisini yeniden düşünüyor', desc: '%76\'sı artan maliyetler ve karmaşıklık nedeniyle genel bulut stratejilerini yeniden değerlendiriyor.' },
  { icon: 'ri-robot-line', title: 'Yapay Zeka Hazırlığı', stat: '%53', statLabel: 'iki yıl içinde AI dağıtmayı planlıyor', desc: '%53\'ü iki yıl içinde özel, on-premises AI altyapısı dağıtmayı planlıyor. Uzmanlaşmış GPU altyapısı gerekiyor.' },
];

const migrationFeatures = [
  'Sorunsuz Migrasyon',
  'Mevcut Donanımı Kullanır',
  'Daha İyi Performans',
  'Veri Dayanıklılığını Artırır',
];

const cloudFeatures = [
  'Kontrolü Alın',
  'Donanım Ömrünü Uzatın',
  'Sürekli Maliyetleri Azaltın',
];

const sdFeatures = [
  'Birleşik Altyapı Öğrenme Eğrisini Kısaltır',
  'Tek Kod Tabanı Karmaşıklığı Azaltır',
  'Entegre AI Altyapı Desteği',
  'Düz BT Yığını, Daha Az Uzman Gerektirir',
];

const infraModJsonLd = { '@context': 'https://schema.org', '@type': 'SoftwareApplication', name: 'VergeOS Altyapı Modernizasyonu', applicationCategory: 'BusinessApplication', operatingSystem: 'Any', description: 'Mevcut altyapınızı VergeOS ile modernize edin. Legacy sistemlerden özel buluta geçiş, maliyet optimizasyonu.', url: `${SITE_URL}/infrastructure-modernization` };

export default function InfrastructureModernizationPage() {
  return (
    <div className="min-h-screen w-full bg-white">
      <SEOHead title="Altyapı Modernizasyonu | Legacy'den Özel Buluta | VergeOS" description="Mevcut altyapınızı VergeOS ile modernize edin. Legacy sistemlerden özel buluta geçiş, maliyet optimizasyonu ve operasyonel verimlilik." keywords="altyapı modernizasyonu, özel bulut, legacy migration, VergeOS, modernizasyon" canonicalPath="/infrastructure-modernization" jsonLd={infraModJsonLd} />
      <AnnouncementBar />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative w-full overflow-hidden bg-[#0f1117] py-24 text-center">
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'repeating-linear-gradient(45deg,#e8600a 0,#e8600a 1px,transparent 0,transparent 50%)', backgroundSize: '20px 20px' }} />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0f1117]/60 to-[#0f1117]/90" />
          <div className="relative z-10 max-w-4xl mx-auto px-6">
            <div className="inline-block bg-[#e8600a]/20 text-[#e8600a] text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-6">BT Girişimleri</div>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
              Altyapı <span className="text-[#e8600a]">Modernizasyonu</span>
            </h1>
            <p className="text-xl text-gray-300 mb-3 font-medium">Yeni altyapı olmadan yeni girişimleri absorbe eden bir mimari oluşturun</p>
          </div>
        </section>

        {/* IT Challenges */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-[#1e293b] text-center mb-4">Bu BT Zorlukları Sizi Geri Mi Tuttu?</h2>
            <p className="text-gray-600 text-center mb-12 text-sm max-w-2xl mx-auto">Günümüz BT ortamının en kritik zorluklarını ele alın</p>
            <div className="grid md:grid-cols-2 gap-6">
              {challenges.map(c => (
                <div key={c.title} className="bg-[#f8fafc] rounded-lg border border-gray-100 p-6 flex gap-4">
                  <span className="w-10 h-10 flex items-center justify-center bg-[#e8600a]/10 rounded-lg text-[#e8600a] shrink-0"><i className={`${c.icon} text-xl`}></i></span>
                  <div>
                    <div className="flex items-baseline gap-2 mb-2">
                      <span className="text-2xl font-extrabold text-[#e8600a]">{c.stat}</span>
                      <span className="text-xs text-gray-500">{c.statLabel}</span>
                    </div>
                    <h3 className="font-semibold text-[#1e293b] mb-1 text-sm">{c.title}</h3>
                    <p className="text-xs text-gray-600 leading-relaxed">{c.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Controlling Costs */}
        <section className="py-20 bg-[#f8fafc]">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl font-bold text-[#1e293b] mb-6">Maliyetleri Kontrol Etme, Karmaşıklığı Azaltma</h2>
                <p className="text-gray-600 mb-6 leading-relaxed">Parçalanmış altyapı; çeşitli silahlı çözümlerden oluşur ve yalnızca operasyonel maliyetleri şişirmekle kalmaz, aynı zamanda iş taleplerine hızlı yanıt verme kabiliyetini de engeller. VergeOS, sanallaştırma, depolama, ağ ve AI veri korumayı tek, yazılım tanımlı platformda birleştirir.</p>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-5">
                  <p className="text-sm text-amber-900 font-medium mb-2">Bu karar noktaları sizi de etkiliyor mu?</p>
                  <ul className="space-y-1">
                    {['Buluta geri dönüş?', 'AI altyapısı kurulumu?', 'Hepsi birden?'].map(item => (
                      <li key={item} className="text-xs text-amber-800 flex items-center gap-2">
                        <i className="ri-checkbox-circle-line text-amber-600"></i>{item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="rounded-xl overflow-hidden">
                <img src="https://public.readdy.ai/ai/img_res/22f226fab35bac9b97ab8e66ab35598e.jpg" alt="Altyapı Modernizasyonu" className="w-full h-full object-top" />
              </div>
            </div>
          </div>
        </section>

        {/* VMware Alternative */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div className="rounded-xl overflow-hidden">
                <img src="https://readdy.ai/api/search-image?query=VMware%20alternative%20migration%20lab%20visualization%20showing%20seamless%20transition%20from%20VMware%20vSphere%20to%20VergeOS%20unified%20platform%2C%20interactive%20hands-on%20lab%20interface%20dark%20background%20orange%20highlights%20professional%20enterprise%20migration%20technology&width=580&height=380&seq=infra_mod_vmware1&orientation=landscape" alt="VMware Alternatifi" className="w-full h-full object-top" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-[#1e293b] mb-6">Bir Alternatif</h2>
                <p className="text-gray-600 mb-6 leading-relaxed">Geleceği çevreleyen belirsizlik, birçok BT departmanını sanallaştırma stratejileri konusunda çelişkili bir durumda bırakmaktadır. VergeOS, güvenilir ve ekonomik bir alternatif sunar.</p>
                <ul className="space-y-2 mb-6">
                  {migrationFeatures.map(item => (
                    <li key={item} className="flex items-center gap-2 text-sm text-gray-700">
                      <i className="ri-check-line text-[#e8600a] text-base"></i>{item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Cloud Chaos */}
        <section className="py-20 bg-[#f8fafc]">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl font-bold text-[#1e293b] mb-6">Bulut Karmaşasını Ortadan Kaldırın</h2>
                <p className="text-gray-600 mb-6 leading-relaxed">Genel bulut esneklik ve ölçeklenebilirlik sunarken, gizli maliyetler ve karmaşıklık birçok kuruluşu bütçe aşımı ve yönetim baş ağrısına sürüklüyor. VergeOS, veri merkezinizde bulut benzeri yetenekleri etkinleştirerek çözüm sunar.</p>
                <ul className="space-y-2">
                  {cloudFeatures.map(item => (
                    <li key={item} className="flex items-center gap-2 text-sm text-gray-700">
                      <i className="ri-check-line text-[#e8600a] text-base"></i>{item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-xl overflow-hidden">
                <img src="https://readdy.ai/api/search-image?query=private%20cloud%20architecture%20showing%20VergeOS%20providing%20cloud-like%20capabilities%20on-premises%20with%20full%20visibility%20and%20control%2C%20hybrid%20and%20multi-cloud%20strategy%20simplification%20diagram%2C%20dark%20background%20orange%20highlights%20professional%20enterprise%20cloud%20visualization&width=580&height=380&seq=infra_mod_cloud1&orientation=landscape" alt="Bulut Karmaşasını Ortadan Kaldırın" className="w-full h-full object-top" />
              </div>
            </div>
          </div>
        </section>

        {/* Private AI */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div className="rounded-xl overflow-hidden">
                <img src="https://readdy.ai/api/search-image?query=private%20enterprise%20AI%20unified%20architecture%20showing%20VergeIQ%20integrated%20into%20VergeOS%20with%20virtual%20data%20centers%20AI%20workloads%20storage%20and%20networking%20all%20running%20together%2C%20VergeIQ%20chatbot%20interface%20and%20infrastructure%20management%20on%20unified%20platform%2C%20dark%20background%20orange%20highlights&width=580&height=400&seq=infra_mod_ai1&orientation=landscape" alt="Özel Kurumsal AI" className="w-full h-full object-top" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-[#1e293b] mb-6">Özel Kurumsal AI — Birleşik</h2>
                <p className="text-gray-600 mb-6 leading-relaxed">AI iş yükleri, yüksek performans, güvenilirlik ve ölçeklenebilirlik gerektiren özel altyapı ister. VergeOS, AI iş yüklerini mevcut altyapınıza sorunsuz entegre eder; ayrı, özelleştirilmiş altyapıya ihtiyaç duymaksızın gerekli ölçeklenebilirlik ve performansı sağlar.</p>
                <ul className="space-y-2 mb-6">
                  {['Gün 1\'den itibaren değer', 'Altyapınızla sohbet edin', 'Özel kaynak kodunu analiz edin', 'Belgeler özetleyin, içerik oluşturun'].map(item => (
                    <li key={item} className="flex items-center gap-2 text-sm text-gray-700">
                      <i className="ri-check-line text-[#e8600a] text-base"></i>{item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Software Defined */}
        <section className="py-20 bg-[#f8fafc]">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl font-bold text-[#1e293b] mb-6">Basitleştirilmiş Altyapı Yazılım Tanımlıdır</h2>
                <p className="text-gray-600 mb-6 leading-relaxed">BT altyapısının artan karmaşıklığı, yüksek düzeyde uzmanlaşmış beceriler gerektirir ve nitelikli personeli işe almak ve elde tutmak giderek daha zor ve pahalı hale gelmektedir. VergeOS altyapı yönetimini dramatik biçimde basitleştirir.</p>
                <ul className="space-y-3">
                  {sdFeatures.map(item => (
                    <li key={item} className="flex items-start gap-2 text-sm text-gray-700">
                      <i className="ri-check-line text-[#e8600a] text-base shrink-0 mt-0.5"></i>{item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-xl overflow-hidden">
                <img src="https://readdy.ai/api/search-image?query=software%20defined%20infrastructure%20comparison%20showing%20multiple%20virtual%20data%20centers%20on%20VergeOS%20unified%20platform%20versus%20legacy%20complex%20multi-team%20multi-tool%20infrastructure%2C%20simplification%20visualization%20dark%20background%20orange%20highlights%20professional%20enterprise%20technology%20diagram&width=580&height=380&seq=infra_mod_sd1&orientation=landscape" alt="Yazılım Tanımlı Altyapı" className="w-full h-full object-top" />
              </div>
            </div>
          </div>
        </section>

      </main>
      <FooterSection />
    </div>
  );
}
